import React, {FC, useCallback, useMemo, useState, ChangeEvent, useEffect} from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import {useTranslation} from 'react-i18next';
import {ChecklistItem} from '../../../types';
import {formatChecklist} from '../../../helpers';

type ChecklistProps = {
  items: Nullable<ChecklistItem[]>,
  setIsChecklistCompleted: (completed: boolean) => void,
  isDisabled: boolean,
  notRunningStatus: boolean,
}

const Checklist: FC<ChecklistProps> = ({
  items, setIsChecklistCompleted, isDisabled, notRunningStatus
}) => {
  const {t} = useTranslation('tasks');
  const formattedItems = useMemo(() => formatChecklist(items), [items]);
  const [checklist, setChecklist] = useState<string[]>([]);

  const isCompleted = formattedItems.length === checklist.length;

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const {checked, name} = e.target;

    if (checked) {
      return setChecklist([...checklist, name]);
    }

    setChecklist(checklist.filter((checkItem) => checkItem !== name));
  }, [checklist]);

  useEffect(() => {
    setIsChecklistCompleted(isCompleted);
  }, [isCompleted]);

  useEffect(() => {
    setChecklist([]);
  }, [items]);

  if (!formattedItems.length) {
    return null;
  }

  return (
    <div className="checklist">
      <h3 className="checklist__title">{t('checklist').toUpperCase()}</h3>
      <p className="checklist__hint">{t('checklistHint')}</p>

      <div className="checklist__container">
        <ul className="checklist__list">
          {formattedItems.map(({id, message}) => {
            const itemId = `checklist-item-${id}`;

            return (
              <li key={itemId} className="checklist__item">
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      id={itemId}
                      name={itemId}
                      onChange={handleChange}
                      disabled={isDisabled}
                      checked={notRunningStatus ? notRunningStatus : undefined}
                    />
                  )}
                  label={message}
                />
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default Checklist;
